import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { sanitizeText } from '../../lib/htmlSanitizer';

/**
 * Full-screen block shown to non-owner teammates when their workspace
 * owner's trial has expired AND they don't have an active subscription.
 *
 * Triggered two ways:
 *   1. On bootstrap: user.ownerSubscriptionExpired === true (set by the
 *      gating middleware on /auth/me).
 *   2. Mid-session: axios catches a 402 with reason
 *      'owner_subscription_required' and dispatches
 *      'auth:owner-subscription-required'. The App-level listener flips
 *      a state flag that survives until the next /auth/me refresh.
 *
 * No in-app navigation. Sign-out is the only action — the teammate has
 * to wait for their owner to subscribe before they can keep using
 * Lanura.
 */
const OwnerSubscriptionRequiredBlock: React.FC = () => {
  const { user, logout } = useAuth() as any;
  const ownerEmail: string = user?.ownerEmail || 'your workspace owner';

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'linear-gradient(135deg, #f0fdfa, #ecfeff)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 560,
          width: '100%',
          background: '#fff',
          borderRadius: 20,
          padding: '36px 32px',
          boxShadow: '0 12px 40px rgba(15, 23, 42, 0.10)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'rgba(245, 158, 11, 0.10)',
            color: '#b45309',
            padding: '4px 12px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            marginBottom: 18,
          }}
        >
          Subscription needed
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 12px', color: '#0f172a' }}>
          {user?.name ? `Hi ${sanitizeText(String(user.name)).split(' ')[0]} - ` : ''}your workspace
          is paused.
        </h1>
        <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: '0 0 24px' }}>
          Your workspace owner's free trial has ended. To keep using Lanura, ask{' '}
          <strong>{ownerEmail}</strong> to subscribe to a paid plan.
        </p>
        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 28px' }}>
          Your sessions, recordings, and pathways are all safely stored. They'll come right back as
          soon as your owner subscribes.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 14, fontSize: 13 }}>
          <a
            href={`mailto:${ownerEmail}?subject=${encodeURIComponent('Lanura subscription needed')}`}
            style={{
              background: '#0f7a7d',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '10px 20px',
              fontSize: 14,
              fontWeight: 700,
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            Email your owner
          </a>
          <button
            type="button"
            onClick={async () => {
              try {
                await logout();
              } catch (_) {}
              window.location.href = '/login';
            }}
            style={{
              background: 'transparent',
              border: '1px solid #cbd5e1',
              borderRadius: 10,
              padding: '10px 20px',
              fontSize: 14,
              fontWeight: 600,
              color: '#475569',
              cursor: 'pointer',
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnerSubscriptionRequiredBlock;
